import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { BarChart3, Table2, FileText } from 'lucide-react';
import ThemeSectionCard from './ThemeSectionCard';
import { createTheme, updateTheme } from './themeService';
import { showSuccessToast, showDangerToast } from '../../validation/ToastContainer';
import './theme.scss';

// Section type definitions
const SECTION_TYPES = {
    CHART_COMPLIANCE: { label: 'Compliance Graph', category: 'chart', icon: BarChart3 },
    CHART_MSB: { label: 'MSB Violations Graph', category: 'chart', icon: BarChart3 },
    CHART_DEVICE_STATUS: { label: 'Device Status Graph', category: 'chart', icon: BarChart3 },
    CHART_RISK: { label: 'Risk Score Graph', category: 'chart', icon: BarChart3 },
    TABLE_VIOLATIONS: { label: 'MSB Violations Table', category: 'table', icon: Table2 },
    CUSTOM_HTML: { label: 'Custom Section', category: 'custom', icon: FileText }
};

const ThemeCreatorModal = ({ isOpen, onClose, onSave, editTheme = null }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [sections, setSections] = useState([]);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    // Initialize form when editing
    useEffect(() => {
        if (editTheme) {
            setName(editTheme.name || '');
            setDescription(editTheme.description || '');
            const sortedSections = [...(editTheme.sections || [])].sort((a, b) => a.sectionOrder - b.sectionOrder);
            // Ensure each section has a unique client-side ID for React keying
            // Use pk (from API), id, or generate a unique one
            const sectionsWithIds = sortedSections.map((section, index) => ({
                ...section,
                clientId: section.clientId || section.pk || section.id || `section-${Date.now()}-${index}`
            }));
            setSections(sectionsWithIds);
        } else {
            setName('');
            setDescription('');
            setSections([]);
        }
        setShowErrors(false);
    }, [editTheme, isOpen]);

    // Add a new section
    const handleAddSection = (sectionType) => {
        const newSection = {
            id: Date.now(),
            clientId: `section-${Date.now()}`, // Unique client-side ID for React keying
            sectionOrder: sections.length + 1,
            sectionType,
            sectionName: SECTION_TYPES[sectionType]?.label || 'New Section',
            configJson: JSON.stringify(getDefaultConfig(sectionType)),
            contentHtml: ''
        };
        setSections([...sections, newSection]);
        setShowAddMenu(false);
    };

    // Get default config based on section type
    const getDefaultConfig = (sectionType) => {
        switch (sectionType) {
            case 'CHART_COMPLIANCE':
                return { chartType: sectionType.replace('CHART_', ''), deviceId: null, filterGroups: [] };
            case 'CHART_MSB':
                return {
                    chartType: sectionType.replace('CHART_', ''),
                    deviceId: null,
                    filterGroups: []
                };    
            case 'CHART_DEVICE_STATUS':
                return { chartType: 'DEVICES_STATUS', statusFilter: 'ALL', filterGroups: [] };
            case 'CHART_RISK':
                return { chartType: 'RISK_SCORE', severityFilter: 'ALL', filterGroups: [] };
            case 'TABLE_VIOLATIONS':
                return {
                    columns: ['RULE_NAME', 'DESCRIPTION', 'USER', 'SEVERITY', 'VALID'],
                    filterGroups: [
                        { targetType: '', targetTypeValue: '' }
                    ]
                };
            default:
                return {};
        }
    };

    // Update section config
    const handleUpdateSection = (index, updates) => {
        const updatedSections = [...sections];
        updatedSections[index] = { ...updatedSections[index], ...updates };
        setSections(updatedSections);
    };

    // Delete a section
    const handleDeleteSection = (index) => {
        const updatedSections = sections.filter((_, i) => i !== index);
        // Update order
        updatedSections.forEach((s, i) => s.sectionOrder = i + 1);
        setSections(updatedSections);
    };

    // Move section up/down
    const handleMoveSection = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= sections.length) return;

        const updatedSections = [...sections];
        const temp = updatedSections[index];
        updatedSections[index] = updatedSections[newIndex];
        updatedSections[newIndex] = temp;

        // Update order
        updatedSections.forEach((s, i) => s.sectionOrder = i + 1);
        setSections(updatedSections);
    };

    // Save theme
    const handleSave = async () => {
        if (!name.trim()) {
            setShowErrors(true);
            showDangerToast('Template name is required', 'Validation Error');
            return;
        }

        if (sections.length === 0) {
            showDangerToast('Please add at least one section', 'Validation Error');
            return;
        }

        // Validate only incomplete selected filters; blank table filters are optional.
        const requiresExplicitValue = ['RuleName', 'RuleDescription', 'Severity', 'Valid'];
        for (let si = 0; si < sections.length; si++) {
            const s = sections[si];
            if (s.sectionType === 'TABLE_VIOLATIONS') {
                const config = typeof s.configJson === 'string' ? JSON.parse(s.configJson) : (s.configJson || {});
                const groups = config.filterGroups || [];
                for (let gi = 0; gi < groups.length; gi++) {
                    const g = groups[gi];
                    if (!g.targetType) {
                        continue;
                    }
                    // Only error on empty value for types that require explicit selection
                    if (requiresExplicitValue.includes(g.targetType) && !g.targetTypeValue) {
                        showDangerToast(`Section "${s.sectionName || 'Violations Table'}": Filter row ${gi + 1} has no value for "${g.targetType}".`, 'Validation Error');
                        return;
                    }
                }
            }
        }

        setIsSaving(true);

        try {
            const themeDto = {
                name: name.trim(),
                description: description.trim(),
                sections: sections.map((s, index) => ({
                    sectionOrder: index + 1,
                    sectionType: s.sectionType,
                    sectionName: s.sectionName,
                    configJson: s.configJson,
                    contentHtml: s.contentHtml
                }))
            };

            let response;
            if (editTheme?.id) {
                response = await updateTheme(editTheme.id, themeDto);
            } else {
                response = await createTheme(themeDto);
            }

            if (response.ok) {
                const savedTheme = await response.json();
                showSuccessToast(
                    `Template "${name}" ${editTheme ? 'updated' : 'created'} successfully`,
                    'Success'
                );
                onSave?.(savedTheme);
                onClose();
            } else {
                const error = await response.json();
                showDangerToast(error.message || 'Failed to save theme', 'Error');
            }
        } catch (err) {
            console.error('Error saving theme:', err);
            showDangerToast('An unexpected error occurred', 'Error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal oct_display-block modal-v2-root">
            <div className="modal oct_modal modal-v2-overlay">
                <div className="modal-dialog modal-xl modal-dialog-centered modal-v2-dialog" aria-modal="true">
                    <div className="modal-content modal-v2-shell create-report-modal theme-creator-modal">
                        <div className="modal-v2-header theme-creator-modal-header">
                            <div className="modal-v2-header-top">
                                <div className="modal-v2-header-copy">
                                    <h2 className="modal-v2-heading theme-creator-modal-title">{editTheme ? 'Edit Report Template' : 'Create Report Template'}</h2>
                                    <p className="modal-v2-subheading theme-creator-modal-subtitle">Design your custom report template</p>
                                </div>
                            </div>
                            <button type="button" className="modal-v2-close theme-creator-modal-close" aria-label="Close" onClick={onClose}>
                                <span aria-hidden="true"><CloseIcon /></span>
                            </button>
                        </div>
                        <div className="modal-body modal-v2-body theme-creator-modal-body">
                            <form
                                id="theme-creator-form"
                                className="theme-creator-form"
                                autoComplete="off"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    handleSave();
                                }}
                            >
                                <section className="theme-creator-section">
                                    <div className="theme-creator-section-head">
                                        <h3 className="theme-creator-section-title">Template Details</h3>
                                    </div>
                                    <div className="theme-creator-grid">
                                        <div className="theme-creator-field">
                                            <label htmlFor="themeName">Template Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                id="themeName"
                                                className={`theme-creator-input ${showErrors && !name ? 'is-invalid' : ''}`}
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="Enter template name"
                                            />
                                        </div>

                                        <div className="theme-creator-field">
                                            <label htmlFor="themeDescription">Description</label>
                                            <input
                                                type="text"
                                                id="themeDescription"
                                                className="theme-creator-input"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                placeholder="Enter description"
                                            />
                                        </div>
                                    </div>
                                </section>

                                <section className="theme-creator-section">
                                    <div className="theme-creator-sections-toolbar">
                                        <h3 className="theme-creator-section-title">Report Sections ({sections.length})</h3>
                                        <div className="add-section-dropdown">
                                            <button
                                                type="button"
                                                className="theme-creator-add-btn"
                                                onClick={() => setShowAddMenu(!showAddMenu)}
                                            >
                                                <AddIcon fontSize="small" /> ADD SECTION
                                            </button>
                                        </div>
                                    </div>

                                    {showAddMenu && (
                                        <div className="add-section-picker">
                                            <p className="add-section-picker-title">Select Section Type:</p>
                                            <div className="add-section-picker-grid">
                                                {Object.entries(SECTION_TYPES).map(([key, value]) => {
                                                    const SectionIcon = value.icon || FileText;
                                                    return (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            className="add-section-menu-item add-section-picker-item"
                                                            onClick={() => handleAddSection(key)}
                                                        >
                                                            <SectionIcon size={18} />
                                                            <span>{value.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {sections.length === 0 ? (
                                        <div className="sections-empty-state">
                                            <p>No sections added yet. Click "ADD SECTION" to get started.</p>
                                        </div>
                                    ) : (
                                        <div className="sections-list">
                                            {sections.map((section, index) => (
                                                <ThemeSectionCard
                                                    key={section.clientId}
                                                    section={section}
                                                    index={index}
                                                    totalSections={sections.length}
                                                    onUpdate={(updates) => handleUpdateSection(index, updates)}
                                                    onDelete={() => handleDeleteSection(index)}
                                                    onMove={(direction) => handleMoveSection(index, direction)}
                                                    sectionTypes={SECTION_TYPES}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </section>

                            </form>
                        </div>
                        <div className="modal-footer modal-v2-footer theme-actions">
                            <div className="modal-v2-footer-note">
                                <span className="modal-v2-footer-note-star">*</span>
                                Required fields
                            </div>
                            <div className="modal-v2-footer-actions">
                                <button type="button" className="modal-v2-btn modal-v2-btn-outline theme-creator-btn theme-creator-btn-cancel" onClick={onClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    form="theme-creator-form"
                                    className="modal-v2-btn modal-v2-btn-primary theme-creator-btn theme-creator-btn-save"
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : (editTheme ? 'Update Template' : 'Save Template')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThemeCreatorModal;
